import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Play, Pause, Volume2,
  FileText, Download, ExternalLink, Link as LinkIcon, CheckCircle2,
  XCircle, Presentation, Paperclip, RotateCcw, Maximize2,
} from "lucide-react";
import { API, apiPost } from "../lib/api";

const P = {
  bg: "#F6F4FB",
  card: "#FFFFFF",
  primary: "#5A3FC0",
  primaryDark: "#43309C",
  primarySoft: "#EDE7FF",
  text: "#2A1F4A",
  textSoft: "#5D5477",
  border: "#E8E1FA",
  success: "#1A8A53",
  danger: "#C4314B",
  gold: "#B8862F",
};

const assetSrc = (a) =>
  a?.asset_id ? `${API}/vip/cms/assets/${a.asset_id}` : null;

const resolveUrl = (data) => {
  const a = data?.asset;
  if (a?.asset_id) return assetSrc(a);
  return data?.url || null;
};

function toEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function BlockShell({ title, children, testId }) {
  return (
    <section data-testid={testId} className="mb-8">
      {title ? (
        <h2 className="font-serif text-xl sm:text-2xl mb-3 leading-tight"
            style={{ color: P.text }}>
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

// ───────────────────────── HTML ─────────────────────────
function HtmlBlock({ block }) {
  return (
    <BlockShell title={block.title} testId={`block-html-${block.id}`}>
      <div className="cms-html"
           dangerouslySetInnerHTML={{ __html: block.data?.html || "" }} />
    </BlockShell>
  );
}

// ───────────────────────── Video ─────────────────────────
function VideoBlock({ block }) {
  const d = block.data || {};
  const mode = d.mode || (d.asset ? "upload" : d.iframe_html ? "iframe" : "url");
  return (
    <BlockShell title={block.title} testId={`block-video-${block.id}`}>
      <div className="rounded-2xl overflow-hidden shadow-lg"
           style={{ border: `1px solid ${P.border}`, background: "#0C0A18" }}>
        {mode === "upload" && d.asset ? (
          <video controls playsInline preload="metadata"
                 controlsList="nodownload"
                 className="w-full block" style={{ maxHeight: 560 }}
                 src={assetSrc(d.asset)} />
        ) : mode === "iframe" && d.iframe_html ? (
          <div className="cms-embed"
               dangerouslySetInnerHTML={{ __html: d.iframe_html }} />
        ) : d.url ? (
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe title={block.title || "Video"} src={toEmbedUrl(d.url)}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0" allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
        ) : (
          <p className="text-sm p-6 text-white/60">Video non disponibile.</p>
        )}
      </div>
    </BlockShell>
  );
}

// ───────────────────────── Audio ─────────────────────────
const SPEEDS = [1, 1.25, 1.5, 2];

function AudioBlock({ block }) {
  const d = block.data || {};
  const src = resolveUrl(d);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [vol, setVol] = useState(1);
  const [speedIdx, setSpeedIdx] = useState(0);

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause(); else a.play();
  };
  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };
  if (!src) return null;
  return (
    <BlockShell title={block.title} testId={`block-audio-${block.id}`}>
      <div className="rounded-2xl p-5 shadow-sm"
           style={{ background: P.card, border: `1px solid ${P.border}` }}>
        <audio ref={audioRef} src={src} preload="metadata"
               onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
               onTimeUpdate={(e) => setCur(e.target.currentTime)}
               onLoadedMetadata={(e) => setDur(e.target.duration)}
               onEnded={() => setPlaying(false)} />
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={toggle} data-testid={`audio-play-${block.id}`}
                  className="h-12 w-12 rounded-full grid place-items-center text-white shrink-0 transition-transform hover:scale-105"
                  style={{ background: P.primary }}>
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>
          <div className="flex-1 min-w-[180px]">
            <input type="range" min={0} max={dur || 0} step={0.5} value={cur}
                   data-testid={`audio-seek-${block.id}`}
                   onChange={(e) => {
                     const v = Number(e.target.value);
                     setCur(v);
                     if (audioRef.current) audioRef.current.currentTime = v;
                   }}
                   className="w-full accent-[#5A3FC0]" />
            <div className="flex justify-between text-[11px] tabular-nums mt-1"
                 style={{ color: P.textSoft }}>
              <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4" style={{ color: P.textSoft }} />
            <input type="range" min={0} max={1} step={0.05} value={vol}
                   onChange={(e) => {
                     const v = Number(e.target.value);
                     setVol(v);
                     if (audioRef.current) audioRef.current.volume = v;
                   }}
                   className="w-20 accent-[#5A3FC0]" />
          </div>
          <button onClick={cycleSpeed} data-testid={`audio-speed-${block.id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold tabular-nums"
                  style={{ background: P.primarySoft, color: P.primary }}>
            {SPEEDS[speedIdx]}×
          </button>
        </div>
      </div>
    </BlockShell>
  );
}

// ───────────────────────── Slides ─────────────────────────
const BACKEND_ORIGIN = API.replace(/\/api$/, "");

function SlidesBlock({ block }) {
  const d = block.data || {};
  const asset = d.asset;
  const src = resolveUrl(d);
  if (!src) return null;
  // PPT/PPTX (and other Office files) render through the Microsoft Office
  // embed viewer, which needs the signed public URL provided by the backend.
  const isOffice = !!asset?.is_office && !!asset?.public_url;
  const officeSrc = isOffice
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        `${BACKEND_ORIGIN}${asset.public_url}`)}`
    : null;
  const isPdf = !isOffice && (!!asset || (d.url || "").toLowerCase().includes(".pdf"));
  const frameSrc = isOffice ? officeSrc : isPdf ? `${src}#view=FitH` : src;
  const openHref = isOffice ? officeSrc : src;
  const previewImg = d.preview_asset?.asset_id
    ? assetSrc(d.preview_asset)
    : (d.preview_url || null);
  return (
    <BlockShell title={block.title || "Diapositive"} testId={`block-slides-${block.id}`}>
      <div className="rounded-2xl overflow-hidden shadow-sm"
           style={{ border: `1px solid ${P.border}`, background: P.card }}>
        {previewImg ? (
          <a href={openHref} target="_blank" rel="noreferrer"
             data-testid={`slides-preview-open-${block.id}`}
             className="group relative block">
            <img src={previewImg} alt={block.title || "Diapositive"}
                 className="w-full object-cover block"
                 style={{ maxHeight: 420 }} />
            <div className="absolute inset-0 flex items-center justify-center transition-colors"
                 style={{ background: "rgba(20,12,40,0.28)" }}>
              <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-lg transition-transform group-hover:scale-105"
                    style={{ background: P.primary }}>
                <Presentation className="h-4 w-4" /> Apri le diapositive
              </span>
            </div>
          </a>
        ) : (
          <iframe title={block.title || "Diapositive"}
                  src={frameSrc}
                  className="w-full" style={{ height: 520, border: 0 }} />
        )}
        <div className="flex items-center justify-between px-4 py-2.5 gap-2 flex-wrap"
             style={{ borderTop: `1px solid ${P.border}` }}>
          <span className="text-xs inline-flex items-center gap-1.5"
                style={{ color: P.textSoft }}>
            <Presentation className="h-3.5 w-3.5" /> Diapositive della lezione
          </span>
          <div className="flex items-center gap-2">
            {isOffice && asset ? (
              <a href={`${src}?download=1`}
                 data-testid={`slides-download-${block.id}`}
                 className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
                 style={{ background: "white", color: P.primary, border: `1px solid ${P.border}` }}>
                <Download className="h-3.5 w-3.5" /> Scarica PPTX
              </a>
            ) : null}
            <a href={openHref} target="_blank" rel="noreferrer"
               data-testid={`slides-fullscreen-${block.id}`}
               className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
               style={{ background: P.primarySoft, color: P.primary }}>
              <Maximize2 className="h-3.5 w-3.5" /> Apri a schermo intero
            </a>
          </div>
        </div>
      </div>
    </BlockShell>
  );
}

// ───────────────────────── Downloads / files / links ─────────────────────────
function ResourceRow({ name, description, url, downloadUrl, testId }) {
  return (
    <div data-testid={testId}
         className="flex items-center gap-4 px-4 py-3 rounded-xl transition-colors hover:bg-[#F4F0FE]"
         style={{ border: `1px solid ${P.border}`, background: P.card }}>
      <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
           style={{ background: P.primarySoft }}>
        <FileText className="h-5 w-5" style={{ color: P.primary }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug" style={{ color: P.text }}>{name}</p>
        {description ? (
          <p className="text-xs mt-0.5 leading-relaxed" style={{ color: P.textSoft }}>{description}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
             style={{ background: P.primarySoft, color: P.primary }}>
            <ExternalLink className="h-3.5 w-3.5" /> Apri
          </a>
        ) : null}
        {downloadUrl ? (
          <a href={downloadUrl}
             className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white"
             style={{ background: P.primary }}>
            <Download className="h-3.5 w-3.5" /> Scarica
          </a>
        ) : null}
      </div>
    </div>
  );
}

function FileLikeBlock({ block }) {
  const d = block.data || {};
  const src = resolveUrl(d);
  if (!src) return null;
  const dl = d.asset ? `${src}?download=1` : null;
  return (
    <BlockShell title={block.title} testId={`block-${block.type}-${block.id}`}>
      <ResourceRow name={d.name || d.asset?.filename || "Documento"}
                   description={d.description}
                   url={src} downloadUrl={dl || src}
                   testId={`file-row-${block.id}`} />
    </BlockShell>
  );
}

function LinkBlock({ block }) {
  const d = block.data || {};
  if (!d.url) return null;
  return (
    <BlockShell title={block.title} testId={`block-link-${block.id}`}>
      <a href={d.url} target="_blank" rel="noreferrer"
         data-testid={`link-row-${block.id}`}
         className="flex items-center gap-4 px-4 py-3 rounded-xl transition-colors hover:bg-[#F4F0FE]"
         style={{ border: `1px solid ${P.border}`, background: P.card }}>
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
             style={{ background: "#FFF7E6" }}>
          <LinkIcon className="h-5 w-5" style={{ color: P.gold }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium" style={{ color: P.text }}>
            {d.label || d.url}
          </p>
          {d.description ? (
            <p className="text-xs mt-0.5" style={{ color: P.textSoft }}>{d.description}</p>
          ) : null}
        </div>
        <ExternalLink className="h-4 w-4 shrink-0" style={{ color: P.primary }} />
      </a>
    </BlockShell>
  );
}

function ResourcesBlock({ block }) {
  const items = block.data?.items || [];
  if (!items.length) return null;
  return (
    <BlockShell title={block.title || "Materiali della lezione"}
                testId={`block-resources-${block.id}`}>
      <div className="space-y-2">
        {items.map((it, i) => {
          const src = it.asset?.asset_id ? assetSrc(it.asset) : it.url || null;
          if (!src) return null;
          const dl = it.asset?.asset_id ? `${src}?download=1` : src;
          return (
            <ResourceRow key={i} name={it.name || it.asset?.filename || `Risorsa ${i + 1}`}
                         description={it.description} url={src} downloadUrl={dl}
                         testId={`resource-row-${block.id}-${i}`} />
          );
        })}
      </div>
    </BlockShell>
  );
}

// ───────────────────────── Quiz ─────────────────────────
function QuizBlock({ block, lessonId }) {
  const questions = block.data?.questions || [];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = useMemo(
    () => questions.reduce(
      (n, q, i) => n + (answers[i] === q.correct_index ? 1 : 0), 0),
    [answers, questions]
  );
  if (!questions.length) return null;

  const submit = async () => {
    setSubmitted(true);
    try {
      await apiPost(`/vip/lessons/${lessonId}/progress`, {
        field: "questions_score",
        value: { correct: score, total: questions.length },
      });
    } catch { /* non-blocking */ }
  };

  return (
    <BlockShell title={block.title || "Metti alla prova"}
                testId={`block-quiz-${block.id}`}>
      <div className="space-y-4">
        {questions.map((q, qi) => (
          <div key={qi} className="rounded-2xl p-5 shadow-sm"
               style={{ background: P.card, border: `1px solid ${P.border}` }}>
            <p className="text-sm font-medium mb-3 leading-relaxed"
               style={{ color: P.text }}>
              <span className="font-semibold mr-1.5" style={{ color: P.primary }}>
                {qi + 1}.
              </span>
              {q.stem}
            </p>
            <div className="space-y-2">
              {(q.options || []).map((opt, oi) => {
                const chosen = answers[qi] === oi;
                const correct = submitted && oi === q.correct_index;
                const wrong = submitted && chosen && oi !== q.correct_index;
                return (
                  <button key={oi} disabled={submitted}
                          data-testid={`quiz-${block.id}-q${qi}-opt${oi}`}
                          onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                          className="w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors flex items-start gap-2.5"
                          style={{
                            border: `1.5px solid ${correct ? P.success : wrong ? P.danger : chosen ? P.primary : P.border}`,
                            background: correct ? "#EAF7F0" : wrong ? "#FDECEF" : chosen ? P.primarySoft : "white",
                            color: P.text,
                          }}>
                    <span className="font-semibold shrink-0"
                          style={{ color: correct ? P.success : wrong ? P.danger : P.primary }}>
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span className="leading-relaxed">{opt}</span>
                    {correct ? <CheckCircle2 className="h-4 w-4 ml-auto shrink-0 mt-0.5" style={{ color: P.success }} /> : null}
                    {wrong ? <XCircle className="h-4 w-4 ml-auto shrink-0 mt-0.5" style={{ color: P.danger }} /> : null}
                  </button>
                );
              })}
            </div>
            {submitted && q.explanation ? (
              <div className="mt-3 px-4 py-3 rounded-xl text-sm leading-relaxed"
                   style={{ background: "#FBF7EC", borderLeft: `3px solid ${P.gold}`, color: P.textSoft }}>
                <span className="font-semibold" style={{ color: P.text }}>Spiegazione: </span>
                {q.explanation}
              </div>
            ) : null}
          </div>
        ))}
        {!submitted ? (
          <button onClick={submit}
                  data-testid={`quiz-submit-${block.id}`}
                  disabled={Object.keys(answers).length < questions.length}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity disabled:opacity-40"
                  style={{ background: P.primary }}>
            Verifica le risposte
          </button>
        ) : (
          <div className="flex items-center justify-between px-5 py-4 rounded-2xl"
               style={{ background: score === questions.length ? "#EAF7F0" : P.primarySoft }}>
            <p className="text-sm font-semibold" style={{ color: P.text }}
               data-testid={`quiz-score-${block.id}`}>
              Risultato: {score}/{questions.length}
            </p>
            <button onClick={() => { setAnswers({}); setSubmitted(false); }}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
                    style={{ background: "white", color: P.primary, border: `1px solid ${P.border}` }}>
              <RotateCcw className="h-3.5 w-3.5" /> Riprova
            </button>
          </div>
        )}
      </div>
    </BlockShell>
  );
}

function SeparatorBlock() {
  return (
    <div className="my-10 flex items-center gap-3" aria-hidden>
      <div className="flex-1 h-px" style={{ background: P.border }} />
      <div className="h-1.5 w-1.5 rounded-full" style={{ background: P.primary }} />
      <div className="flex-1 h-px" style={{ background: P.border }} />
    </div>
  );
}

function BlockRenderer({ block, lessonId, onQuizDone }) {
  switch (block.type) {
    case "html": return <HtmlBlock block={block} />;
    case "video": return <VideoBlock block={block} />;
    case "audio": return <AudioBlock block={block} />;
    case "slides": return <SlidesBlock block={block} />;
    case "pdf":
    case "file": return <FileLikeBlock block={block} />;
    case "link": return <LinkBlock block={block} />;
    case "resources": return <ResourcesBlock block={block} />;
    case "quiz": return <QuizBlock block={block} lessonId={lessonId} />;
    case "separator": return <SeparatorBlock />;
    default: return null;
  }
}

// Marks a block as "seen" once it stays in the viewport for ~0.8s.
function ObservedBlock({ blockId, onSeen, seen, children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (seen || !ref.current || typeof IntersectionObserver === "undefined") return;
    let timer = null;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => onSeen(blockId), 800);
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(ref.current);
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
    // eslint-disable-next-line
  }, [seen, blockId]);
  return <div ref={ref} data-block-observed={blockId}>{children}</div>;
}

// Sticky progress bar: "sezioni viste" out of countable blocks.
function BlockProgressBar({ total, seenCount }) {
  const pct = total > 0 ? Math.round((seenCount / total) * 100) : 0;
  const done = pct >= 100;
  return (
    <div data-testid="vip-lesson-block-progress" data-progress-pct={pct}
         className="sticky top-2 z-30 mb-6">
      <div className="rounded-2xl px-4 py-2.5 shadow-md flex items-center gap-3"
           style={{
             background: "rgba(255,255,255,0.92)",
             backdropFilter: "blur(10px)",
             border: `1px solid ${done ? "#BFE6D2" : P.border}`,
           }}>
        <div className="flex-1 h-2 rounded-full overflow-hidden"
             style={{ background: "#EFEAF9" }}>
          <div className="h-full rounded-full transition-[width] duration-700 ease-out"
               style={{
                 width: `${pct}%`,
                 background: done
                   ? "linear-gradient(90deg, #1A8A53, #34B375)"
                   : "linear-gradient(90deg, #5A3FC0, #8B6FE8)",
               }} />
        </div>
        <span className="text-xs font-semibold tabular-nums shrink-0 inline-flex items-center gap-1.5"
              style={{ color: done ? P.success : P.primary }}>
          {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
          {seenCount}/{total} sezioni · {pct}%
        </span>
      </div>
    </div>
  );
}

// ───────────────────────── Full lesson page ─────────────────────────
export default function BlocksLessonView({ lesson, refresh }) {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const completed = !!lesson.progress?.completed_at;

  // ── Per-block "seen" tracking ──
  const countable = useMemo(
    () => (lesson.blocks || []).filter((b) => b.type !== "separator"),
    [lesson.blocks]
  );
  const [seenIds, setSeenIds] = useState(
    () => new Set(lesson.progress?.blocks_seen || [])
  );
  useEffect(() => {
    setSeenIds(new Set(lesson.progress?.blocks_seen || []));
    // eslint-disable-next-line
  }, [lesson.lesson_id]);
  const markSeen = (blockId) => {
    setSeenIds((prev) => {
      if (prev.has(blockId)) return prev;
      apiPost(`/vip/lessons/${lesson.lesson_id}/progress`, {
        field: "block_seen", value: blockId,
      }).catch(() => {});
      const next = new Set(prev);
      next.add(blockId);
      return next;
    });
  };
  const seenCount = countable.filter((b) => seenIds.has(b.id)).length;

  const markCompleted = async () => {
    setCompleting(true);
    try {
      await apiPost(`/vip/lessons/${lesson.lesson_id}/progress`, {
        field: "completed", value: true,
      });
      toast.success("Lezione completata");
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setCompleting(false);
  };

  return (
    <div data-testid="vip-lesson-blocks" className="min-h-screen px-4 sm:px-8 py-8"
         style={{ background: P.bg }}>
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/vip")}
                data-testid="vip-lesson-back"
                className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl mb-6"
                style={{ color: P.primaryDark, background: "rgba(255,255,255,0.7)" }}>
          <ArrowLeft className="h-4 w-4" /> Torna all'Academia
        </button>

        <motion.header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[11px] uppercase tracking-[0.22em] mb-2"
             style={{ color: P.primary }}>
            {lesson.module_title || "Academia VIP"}
            {lesson.lesson_number != null && lesson.lesson_number !== ""
              ? ` · Lezione ${lesson.lesson_number}` : ""}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-2"
              data-testid="vip-lesson-title" style={{ color: P.text }}>
            {lesson.title}
          </h1>
          {lesson.subtitle ? (
            <p className="text-lg italic mb-3" style={{ color: P.primaryDark }}>
              {lesson.subtitle}
            </p>
          ) : null}
          {lesson.summary ? (
            <p className="text-base leading-relaxed mb-4" style={{ color: P.textSoft }}>
              {lesson.summary}
            </p>
          ) : null}
          {lesson.cover_image_url ? (
            <img src={lesson.cover_image_url} alt={lesson.title}
                 className="w-full rounded-2xl object-cover mb-6 shadow-md"
                 style={{ maxHeight: 340 }} />
          ) : null}
        </motion.header>

        <div className="mt-4">
          {countable.length > 0 ? (
            <BlockProgressBar total={countable.length} seenCount={seenCount} />
          ) : null}
          {(lesson.blocks || []).map((b) =>
            b.type === "separator" ? (
              <BlockRenderer key={b.id} block={b} lessonId={lesson.lesson_id} />
            ) : (
              <ObservedBlock key={b.id} blockId={b.id}
                             seen={seenIds.has(b.id)} onSeen={markSeen}>
                <BlockRenderer block={b} lessonId={lesson.lesson_id} />
              </ObservedBlock>
            )
          )}
          {(lesson.blocks || []).length === 0 ? (
            <div className="rounded-2xl p-10 text-center"
                 style={{ background: P.card, border: `1px dashed ${P.border}` }}>
              <Paperclip className="h-8 w-8 mx-auto mb-3" style={{ color: P.primary }} />
              <p className="text-sm" style={{ color: P.textSoft }}>
                Il contenuto di questa lezione sarà disponibile a breve.
              </p>
            </div>
          ) : null}
        </div>

        {/* Complete */}
        <div className="mt-10">
          <button onClick={markCompleted} disabled={completing || completed}
                  data-testid="vip-lesson-complete-btn"
                  className="w-full py-3.5 rounded-2xl font-semibold text-sm transition-all inline-flex items-center justify-center gap-2"
                  style={{
                    background: completed ? "#EAF7F0" : P.primary,
                    color: completed ? P.success : "white",
                    border: completed ? `1px solid #BFE6D2` : "none",
                  }}>
            <CheckCircle2 className="h-4 w-4" />
            {completed ? "Lezione completata" : "Segna come completata"}
          </button>
        </div>

        {/* Prev / Next */}
        <nav className="mt-6 grid grid-cols-2 gap-3" data-testid="vip-lesson-nav">
          {lesson.prev_lesson ? (
            <Link to={`/vip/lessons/${lesson.prev_lesson.lesson_id}`}
                  data-testid="vip-lesson-prev"
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm transition-colors hover:bg-[#F4F0FE]"
                  style={{ background: P.card, border: `1px solid ${P.border}`, color: P.text }}>
              <ChevronLeft className="h-4 w-4 shrink-0" style={{ color: P.primary }} />
              <span className="truncate">{lesson.prev_lesson.title}</span>
            </Link>
          ) : <span />}
          {lesson.next_lesson ? (
            <Link to={`/vip/lessons/${lesson.next_lesson.lesson_id}`}
                  data-testid="vip-lesson-next"
                  className="flex items-center justify-end gap-2 px-4 py-3 rounded-xl text-sm transition-colors hover:bg-[#F4F0FE]"
                  style={{ background: P.card, border: `1px solid ${P.border}`, color: P.text }}>
              <span className="truncate">{lesson.next_lesson.title}</span>
              <ChevronRight className="h-4 w-4 shrink-0" style={{ color: P.primary }} />
            </Link>
          ) : <span />}
        </nav>
      </div>
    </div>
  );
}
